import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type Chatbot } from 'entities/Chatbot'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  EmojiAction,
  EnumVariantMessageField,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
} from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { SavedRepliesStore } from 'widgets/SavedReplies'

export class ChatbotMessage {
  private readonly _disposeFieldReaction: IReactionDisposer | null = null
  private readonly _savedRepliesStore: SavedRepliesStore
  private _isAirCall = false
  private readonly _field: MessageFieldStore

  constructor(private _chatbot: Chatbot, isAirCall: boolean, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this._field = new MessageFieldStore(
      {
        isCurrentAirCall: this._isAirCall,
        placeholder: 'Write your message...',
        styles: {
          minHeight: 106,
        },
        variant: EnumVariantMessageField.Underline,
        showActionsItems: 5,
        resetOnUnmount: false,
        textLimit: {
          maxLength: 1600,
          message: 'Message should not be longer than 1.600 characters',
        },
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeActions: () => [
          { iconButtonComponent: <MediaAction /> },
          { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this._handleMergeField(value)}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
      },
      this._entities
    )
    this._isAirCall = isAirCall
    this._disposeFieldReaction = reaction(
      () => [this._field.text, this._field.attachments] as [string, Attachment[]],
      ([text, attachments]) => {
        const prevAttachments = this._chatbot.firstMessage.attachments
        const attachmentsAreSame =
          prevAttachments.length === attachments.length &&
          prevAttachments.every((attachment) => attachments.includes(attachment))

        const prevText = this._chatbot.firstMessage.message
        const textIsSame = prevText === text

        if (attachmentsAreSame && textIsSame) return

        this._chatbot.firstMessage.sync(text, attachments)
      }
    )
  }

  syncMessage = () => {
    const message = this._chatbot.firstMessage

    this._field.setMessageData({
      message: message.message,
      attachments: message.attachments,
      isReset: false,
      isReplace: true,
    })
  }

  dispose = () => {
    this._disposeFieldReaction?.()
  }

  private _handleMergeField = (field: MergeField) => {
    if (!this._field.addContent) return

    const fieldContent = mergeFieldReplacer(this._entities, field.template) + '&nbsp;'

    this._field.addContent(fieldContent)
  }

  get field() {
    return this._field
  }
}
